import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import {
  getApiErrorMessage,
  getProntuarioId,
  NivelRisco,
  Prontuario,
  ProntuarioStatus,
  ProntuarioUpdatePayload,
  updateProntuario,
} from "src/apis/prontuario";
import { prontuarioStatusLabels, riskLabels } from "./utils";

type ProntuarioGeneralFormProps = {
  prontuario: Prontuario;
  onUpdated: (prontuario: Prontuario) => void;
  onNotify: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
};

type GeneralFormValues = Required<ProntuarioUpdatePayload>;

const normalizeFormValues = (prontuario: Prontuario): GeneralFormValues => ({
  queixa_inicial: String(prontuario.queixa_inicial ?? ""),
  resumo_geral: String(prontuario.resumo_geral ?? ""),
  contexto_clinico: String(prontuario.contexto_clinico ?? ""),
  objetivo_terapeutico: String(prontuario.objetivo_terapeutico ?? ""),
  nivel_risco: (prontuario.nivel_risco as NivelRisco) ?? "nao_informado",
  observacoes_gerais: String(prontuario.observacoes_gerais ?? ""),
  restricoes_importantes: String(prontuario.restricoes_importantes ?? ""),
  contato_emergencia_nome: String(prontuario.contato_emergencia_nome ?? ""),
  contato_emergencia_telefone: String(prontuario.contato_emergencia_telefone ?? ""),
  contato_emergencia_parentesco: String(prontuario.contato_emergencia_parentesco ?? ""),
  medicamentos_em_uso_resumo: String(prontuario.medicamentos_em_uso_resumo ?? ""),
  alergias: String(prontuario.alergias ?? ""),
  condicoes_relevantes: String(prontuario.condicoes_relevantes ?? ""),
  status: (prontuario.status as ProntuarioStatus) ?? "ativo",
});

const buildChangedPayload = (
  currentValues: GeneralFormValues,
  originalValues: GeneralFormValues
): ProntuarioUpdatePayload => {
  return (Object.keys(currentValues) as Array<keyof GeneralFormValues>).reduce<
    ProntuarioUpdatePayload
  >((payload, key) => {
    if (currentValues[key] !== originalValues[key]) {
      payload[key] = currentValues[key] as never;
    }
    return payload;
  }, {});
};

const SectionHeader: FC<{ title: string }> = ({ title }) => (
  <Grid item xs={12}>
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Divider />
    </Box>
  </Grid>
);

const ProntuarioGeneralForm: FC<ProntuarioGeneralFormProps> = ({
  prontuario,
  onUpdated,
  onNotify,
}) => {
  const originalValues = useMemo(() => normalizeFormValues(prontuario), [prontuario]);
  const [formValues, setFormValues] = useState<GeneralFormValues>(originalValues);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setFormValues(originalValues);
  }, [originalValues]);

  const handleChange = (field: keyof GeneralFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const prontuarioId = getProntuarioId(prontuario);
    const payload = buildChangedPayload(formValues, originalValues);

    if (!prontuarioId) {
      onNotify("Não foi possível identificar o prontuário.", "error");
      return;
    }

    if (Object.keys(payload).length === 0) {
      onNotify("Nenhuma alteração para salvar.", "info");
      return;
    }

    setLoading(true);

    try {
      const updatedProntuario = await updateProntuario(prontuarioId, payload);
      onUpdated({ ...prontuario, ...payload, ...updatedProntuario });
      onNotify("Prontuário atualizado com sucesso.", "success");
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível salvar o prontuário."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>

        {/* Dados gerais */}
        <SectionHeader title="Dados gerais" />

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Nível de risco</FormLabel>
            <Select
              value={formValues.nivel_risco}
              onChange={(e) => handleChange("nivel_risco", e.target.value as NivelRisco)}
            >
              {Object.entries(riskLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Status</FormLabel>
            <Select
              value={formValues.status}
              onChange={(e) => handleChange("status", e.target.value as ProntuarioStatus)}
            >
              {Object.entries(prontuarioStatusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <FormGroup>
            <FormLabel>Queixa inicial</FormLabel>
            <TextField
              value={formValues.queixa_inicial}
              onChange={(e) => handleChange("queixa_inicial", e.target.value)}
              multiline
              minRows={2}
            />
          </FormGroup>
        </Grid>

        {/* Quadro clínico */}
        <SectionHeader title="Quadro clínico" />

        {(
          [
            { field: "resumo_geral", label: "Resumo geral" },
            { field: "contexto_clinico", label: "Contexto clínico" },
            { field: "objetivo_terapeutico", label: "Objetivo terapêutico" },
            { field: "observacoes_gerais", label: "Observações gerais" },
            { field: "restricoes_importantes", label: "Restrições importantes" },
            { field: "medicamentos_em_uso_resumo", label: "Medicamentos em uso resumo" },
            { field: "alergias", label: "Alergias" },
            { field: "condicoes_relevantes", label: "Condições relevantes" },
          ] as Array<{ field: keyof GeneralFormValues; label: string }>
        ).map(({ field, label }) => (
          <Grid item xs={12} md={6} key={field}>
            <FormGroup>
              <FormLabel>{label}</FormLabel>
              <TextField
                value={formValues[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                multiline
                minRows={3}
              />
            </FormGroup>
          </Grid>
        ))}

        {/* Contato de emergência */}
        <SectionHeader title="Contato de emergência" />

        <Grid item xs={12} md={4}>
          <FormGroup>
            <FormLabel>Nome</FormLabel>
            <TextField
              value={formValues.contato_emergencia_nome}
              onChange={(e) => handleChange("contato_emergencia_nome", e.target.value)}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormGroup>
            <FormLabel>Telefone</FormLabel>
            <TextField
              value={formValues.contato_emergencia_telefone}
              onChange={(e) => handleChange("contato_emergencia_telefone", e.target.value)}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormGroup>
            <FormLabel>Parentesco</FormLabel>
            <TextField
              value={formValues.contato_emergencia_parentesco}
              onChange={(e) => handleChange("contato_emergencia_parentesco", e.target.value)}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? undefined : <SaveIcon />}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size="1.25rem" />
                Salvando prontuário...
              </Box>
            ) : (
              "Salvar alterações"
            )}
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
};

export default ProntuarioGeneralForm;
